// Расстановка узлов на полотне уз — 04_links.md §2.5, §2.4, 09_tech.md §3.
// Чистая функция от ступени и порядка добавления: позиция не хранится в БД, считается на лету.
import type { Tier } from '../enums.js'

export interface LayoutLinkInput {
  id: string
  /**
   * Чем меньше, тем ближе к первому месту. Это порядок ДОБАВЛЕНИЯ связи (время создания),
   * а не теплота: место на небе закреплено за человеком навсегда.
   *
   * Так было не всегда — раньше места раздавались по убыванию теплоты, и любой лучик
   * перетасовывал созвездие: отправил комплимент маме, а местами поменялись мама с братом
   * (правка основателя). Теплота по-прежнему решает, НАСКОЛЬКО далеко узел от центра
   * (`tier` → радиус), но не КУДА он встанет.
   */
  order: number
  tier: Tier
}

export interface NodePosition {
  id: string
  x: number
  y: number
  /**
   * Коэффициенты места (−1…1) до умножения на радиус. Нужны превью на главной: там своё
   * окно 250px, и радиус считается от его размера, а не от TIER_RADIUS полотна.
   */
  kx: number
  ky: number
  tier: Tier
}

/** Радиус от центра моей звезды по ступени близости. */
export const TIER_RADIUS: Record<Tier, number> = {
  near: 180,
  mid: 250,
  far: 290,
}

// Коэффициенты позиций 1..7 относительно радиуса ступени узла (09_tech.md §3).
// Места заполняются от самой тёплой связи; пустые места не рисуются.
const SLOT_COEFFICIENTS: ReadonlyArray<readonly [number, number]> = [
  [-0.62, -0.78],
  [0.62, -0.78],
  [-0.62, 0.72],
  [0.62, 0.72],
  [0, -1.0],
  [0, 1.0],
  [-1.0, 0],
]

export const MAX_CANVAS_NODES = SLOT_COEFFICIENTS.length

/**
 * Расставляет до 7 узлов: сортирует связи по порядку добавления, каждой присваивает
 * следующее свободное место 1..7, координаты считает от радиуса её собственной ступени.
 */
export const getNodePositions = (links: LayoutLinkInput[]): NodePosition[] => {
  const ordered = [...links].sort((a, b) => a.order - b.order).slice(0, MAX_CANVAS_NODES)

  return ordered.map((link, index) => {
    const slot = SLOT_COEFFICIENTS[index]
    if (!slot) {
      throw new Error(`getNodePositions: нет коэффициента для места ${index + 1}`)
    }
    const [kx, ky] = slot
    const radius = TIER_RADIUS[link.tier]
    return {
      id: link.id,
      x: kx * radius,
      y: ky * radius,
      kx,
      ky,
      tier: link.tier,
    }
  })
}
